import { useState } from 'react'
import type { Emergence, EmergenceType } from '@/types'
import { EmptyEmergencesState } from '@/components/shared/EmptyState'
import { useUpdateEmergence } from '@/hooks/useEmergences'
import { useSynapseStrength } from '@/hooks/useSynapseStrength'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'

interface EmergencesFeedProps {
  emergences: Emergence[]
  onRefresh?: () => void
}

export default function EmergencesFeed({ emergences, onRefresh }: EmergencesFeedProps) {
  const [loading, setLoading] = useState<string | null>(null)
  const [localEmergences, setLocalEmergences] = useState(emergences)
  const { updateStatus } = useUpdateEmergence()
  const { onStrengthen, onDecay } = useSynapseStrength()

  const getEmergenceIcon = (type: EmergenceType) => {
    switch (type) {
      case 'bridge': return '⚡'
      case 'gap': return '⚠️'
      case 'synthesis': return '🧬'
      case 'contradiction': return '🛑'
      case 'question': return '❓'
      default: return '📡'
    }
  }

  const getBorderColor = (type: EmergenceType) => {
     switch (type) {
      case 'bridge': return 'border-domain-tech'
      case 'gap': return 'border-amber-500'
      case 'synthesis': return 'border-rsb-accent'
      case 'contradiction': return 'border-red-500'
      case 'question': return 'border-blue-400'
      default: return 'border-rsb-muted'
    }
  }

  const handleAction = async (emergenceId: string, action: 'explored' | 'accepted' | 'discarded') => {
    setLoading(emergenceId)
    try {
      await updateStatus(emergenceId, action)

      // Strengthen or decay synapses based on action
      const emergence = localEmergences.find(e => e.id === emergenceId)
      if (emergence) {
        if (action === 'accepted') {
          onStrengthen(emergenceId)
        } else if (action === 'discarded') {
          onDecay(emergenceId)
        }
      }

      if (action === 'accepted' || action === 'discarded') {
        setLocalEmergences(prev => prev.filter(e => e.id !== emergenceId))
      } else {
        setLocalEmergences(prev =>
          prev.map(e => e.id === emergenceId ? { ...e, status: action } : e)
        )
      }
      onRefresh?.()
    } catch (error) {
      console.error(`Error ${action}ing emergence:`, error)
    } finally {
      setLoading(null)
    }
  }

  if (localEmergences.length === 0) {
    return (
      <div className="p-4">
        <h3 className="text-xs font-bold text-rsb-muted uppercase tracking-widest mb-4">Neural Signals</h3>
        <EmptyEmergencesState />
      </div>
    )
  }

  return (
    <div className="bg-transparent">
      <div className="flex items-center justify-between px-4 pt-4 mb-4">
        <h3 className="text-xs font-bold text-rsb-muted uppercase tracking-widest flex items-center gap-2">
           <span className="w-1.5 h-1.5 rounded-full bg-rsb-accent animate-ping"></span>
           Signals Detected
        </h3>
        <span className="text-[10px] font-mono text-rsb-muted">{localEmergences.length} ACTIVE</span>
      </div>

      <ScrollArea className="h-[500px] px-4 pb-4">
        <div className="space-y-3">
          {localEmergences.map((emergence) => (
            <div
              key={emergence.id}
              className={`
                relative group bg-white/5 hover:bg-white/10 border-l-2 ${getBorderColor(emergence.emergence_type)}
                rounded-r-lg p-3 transition-all duration-200
              `}
            >
              <div className="flex justify-between items-start mb-1">
                 <div className="flex items-center gap-2">
                   <span className="text-sm">{getEmergenceIcon(emergence.emergence_type)}</span>
                   <h4 className="text-sm font-medium text-white truncate max-w-[200px]">{emergence.title}</h4>
                 </div>
                 <Badge variant="outline" className="text-[10px] font-mono text-rsb-muted border-rsb-muted/30">
                   {Math.round(emergence.confidence * 100)}% CONF
                 </Badge>
              </div>

              <p className="text-xs text-rsb-text-secondary leading-relaxed mb-3 pl-6">
                {emergence.description}
              </p>

              {emergence.status === 'new' && (
                <div className="flex justify-end gap-2 pl-6 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAction(emergence.id, 'explored')}
                    disabled={loading === emergence.id}
                    className="px-2 py-1 bg-white/5 hover:bg-white/10 border-white/10 text-[10px] font-bold uppercase tracking-wider text-rsb-text-secondary hover:text-white h-auto"
                  >
                    Investigate
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAction(emergence.id, 'accepted')}
                    disabled={loading === emergence.id}
                    className="px-2 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/20 text-[10px] font-bold uppercase tracking-wider text-emerald-400 h-auto"
                  >
                    Validate
                  </Button>
                   <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAction(emergence.id, 'discarded')}
                    disabled={loading === emergence.id}
                    className="px-2 py-1 bg-red-500/10 hover:bg-red-500/20 border-red-500/20 text-[10px] font-bold uppercase tracking-wider text-red-400 h-auto"
                  >
                    Dismiss
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}
